/**
 * Screen 1b — "Who are you". Shown to a signed-in partner user with no
 * organisation chosen: pick one of their memberships, or create one.
 *
 * Creating one does four calls in order, because every later screen needs a
 * site to hang off: `POST /org` → `POST /auth/select` (the create leaves the
 * token unscoped) → `POST /sites` (live + test pair) → `POST
 * /sites/:key/origins` for the website given here. Name and website are
 * suggested from the email domain; partner type sets defaults later on.
 *
 * Creating an organisation requires accepting the FinderlyFix Partner Terms
 * (owner decision 2026-09-26): a required checkbox with a link that opens the
 * text in a new tab (so the half-filled form survives), and `POST /org`
 * carries the version it showed. The DPA is not part of it: it is available
 * on request and signed separately.
 *
 * The website does NOT lock anything: a new site is `origin_policy: 'any'`
 * (migration 059), so the key works wherever the snippet is pasted. It names
 * the site, and adding it to the site's list means "Is it live?" can check
 * that page before the widget has loaded there once.
 *
 * "How will homeowners reach you?" (owner feedback 2026-09-28): "On my own
 * website", or "A free page from us" for a person with no website (or who
 * would rather not install). The second hides the website field and, once
 * the organisation exists, opens the installer step with "no website"
 * chosen, where the hosted page (finderlyfix.com/p/…) is created — the page
 * needs the live site key made here, and its address is permanent, so the
 * person picks it there rather than having one minted for them. "Get a free
 * page" on the sign-in screen preselects it (setup/startPath.ts).
 *
 * The page path reads as a contractor's signup (owner decision 2026-10-20):
 * "Tell us about your business", "Business name", "What kind of business?"
 * defaulting to Trade company, and the fee line. The website path keeps the
 * organisation wording and the Property manager default.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../api/errors';
import { PARTNER_TYPES, type PartnerType } from '../api/types';
import { useSession } from '../auth/session';
import { CURRENT_TERMS } from '../legal/current';
import { FeeSummary } from '../legal/FeeSummary';
import { TermsLinks } from '../legal/TermsGate';
import { afterOrgSetup } from '../nextPath';
import { useRouter } from '../router';
import { clearStartPath, presetNoWebsite, startPath, type StartPath } from '../setup/startPath';
import { Choices, ErrorNotice, Field } from '../ui/kit';
import { AuthFrame, PAGE_PITCH, SITE_PITCH } from './SignUp';

export const PARTNER_TYPE_COPY: Record<PartnerType, { label: string; hint: string }> = {
  property_manager: { label: 'Property manager', hint: 'Residents report maintenance issues.' },
  home_warranty: { label: 'Home warranty', hint: 'Avoid dispatches for things owners can fix.' },
  insurer: { label: 'Insurer', hint: 'Triage before a claim.' },
  contractor_network: { label: 'Contractor network', hint: 'Route diagnosed jobs to your pros.' },
  trade_company: { label: 'Trade company', hint: 'Plumbing, HVAC, electrical and the like.' },
  membership: { label: 'Home membership', hint: 'Members get help faster.' },
  media: { label: 'Publisher / media', hint: 'Content sites with a home-repair audience.' },
  retailer: { label: 'Retailer', hint: 'Parts and tools.' },
  other: { label: 'Something else', hint: 'Reseller, white-label or other.' },
};

/** The type each path starts on: a contractor on the page path, a website partner otherwise. */
export const DEFAULT_TYPE: Record<StartPath, PartnerType> = { page: 'trade_company', site: 'property_manager' };

const PERSONAL_DOMAINS = new Set(['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com', 'aol.com', 'proton.me', 'protonmail.com', 'live.com', 'me.com']);

export function suggestFromEmail(email: string | undefined): { name: string; website: string } {
  const domain = (email ?? '').split('@')[1]?.toLowerCase() ?? '';
  if (!domain || PERSONAL_DOMAINS.has(domain)) return { name: '', website: '' };
  const root = domain.split('.').slice(-2, -1)[0] ?? domain;
  const name = root
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  return { name, website: `https://www.${domain}` };
}

const PAGE_OPTION = 'No website needed: finderlyfix.com/p/your-name, with photos, services, reviews and a quote form.';
const SITE_OPTION = 'Add the diagnosis widget to a site you run.';

export function OrgSetup() {
  const { whoami, memberships, selectOrg, createOrg, api, signOut } = useSession();
  const { navigate, path, search } = useRouter();
  const suggestion = useMemo(() => suggestFromEmail(whoami?.email), [whoami?.email]);
  const [name, setName] = useState(suggestion.name);
  const [where, setWhereState] = useState<StartPath>(startPath);
  const [type, setType] = useState<PartnerType>(() => DEFAULT_TYPE[startPath()]);
  const [website, setWebsite] = useState(suggestion.website);
  // Switching paths switches the default type too, unless the person already chose one.
  const setWhere = (next: StartPath) => {
    if (type === DEFAULT_TYPE[where]) setType(DEFAULT_TYPE[next]);
    setWhereState(next);
  };
  const page = where === 'page';
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(false);
  const [creating, setCreating] = useState(memberships.length === 0);
  const [agreed, setAgreed] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!agreed) return;
    setPending(true);
    setError(null);
    try {
      const orgId = await createOrg(name.trim(), type, CURRENT_TERMS, async () => {
        const site = where === 'page' ? '' : website.trim();
        const host = (() => {
          try {
            return new URL(site).hostname.replace(/^www\./, '');
          } catch {
            return '';
          }
        })();
        const created = await api.createSite(host || name.trim() || 'My site');
        if (site) {
          await api.addOrigin(created.live.site_key, site).catch((err: ApiError) => {
            // The org and site exist; the key works without this row, and the
            // list is editable on Sites & keys.
            if (err.code !== 'ORIGIN_ALREADY_REGISTERED' && err.code !== 'ORIGIN_NOT_HTTPS' && err.code !== 'ORIGIN_RESERVED') throw err;
          });
        }
      });
      clearStartPath();
      const next = afterOrgSetup(path, search);
      if (where === 'page' && !next.startsWith('/oauth/')) {
        presetNoWebsite(orgId);
        navigate('/setup/installer', { replace: true });
      } else {
        navigate(next, { replace: true });
      }
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setPending(false);
    }
  };

  if (!creating) {
    return (
      <AuthFrame title="Welcome back.">
        <div className="stack">
          <h2 id="page-title" tabIndex={-1}>
            Choose an organisation
          </h2>
          <div className="choices">
            {memberships.map((m) => (
              <button key={m.partner_id} type="button" className="choice" style={{ textAlign: 'left' }} onClick={() => void selectOrg(m.partner_id)}>
                <span>
                  <span className="t">{m.display_name}</span>
                  <span className="d" style={{ display: 'block' }}>
                    {m.role}
                  </span>
                </span>
              </button>
            ))}
          </div>
          <button type="button" className="btn" onClick={() => setCreating(true)}>
            Create a new organisation
          </button>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={page ? 'Set up your page.' : 'Set up your organisation.'} pitch={page ? PAGE_PITCH : SITE_PITCH}>
      <form className="stack" onSubmit={submit} noValidate>
        <div className="stack-sm">
          <div className="eyebrow">Getting started</div>
          <h2 id="page-title" tabIndex={-1}>
            {page ? 'Tell us about your business' : 'Who are you?'}
          </h2>
          <p className="muted">
            Signed in as {whoami?.email}. You’ll be the owner; {page ? 'add your crew later if you have one.' : 'invite your team later.'}
          </p>
        </div>
        <Field label={page ? 'Business name' : 'Organisation name'} hint={page ? 'As homeowners know it: it’s the name on your page.' : undefined}>
          {(p) => <input {...p} className="input" required value={name} onChange={(e) => setName(e.target.value)} autoComplete="organization" />}
        </Field>
        <Field label={page ? 'What kind of business?' : 'What kind of organisation?'} hint={PARTNER_TYPE_COPY[type].hint}>
          {(p) => (
            <select {...p} className="input" value={type} onChange={(e) => setType(e.target.value as PartnerType)}>
              {PARTNER_TYPES.map((t) => (
                <option key={t} value={t}>
                  {PARTNER_TYPE_COPY[t].label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Choices<StartPath>
          legend="How will homeowners reach you?"
          value={where}
          onChange={setWhere}
          options={
            page
              ? [
                  { value: 'page', title: 'A free page from us', description: PAGE_OPTION },
                  { value: 'site', title: 'On my own website', description: SITE_OPTION },
                ]
              : [
                  { value: 'site', title: 'On my own website', description: SITE_OPTION },
                  { value: 'page', title: 'A free page from us', description: PAGE_OPTION },
                ]
          }
        />
        {page ? <FeeSummary /> : null}
        {where === 'site' ? (
          <Field label="Your website" hint="We use it to name your site and check it’s live; your key works on any website you paste it into." optional>
            {(p) => (
              <input
                {...p}
                className="input"
                inputMode="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://www.example.com"
              />
            )}
          </Field>
        ) : null}
        <label className="check" htmlFor="org-terms-agree">
          <input id="org-terms-agree" type="checkbox" required checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>
            I agree to the <TermsLinks /> on behalf of this organisation, and I am authorised to do so.
          </span>
        </label>
        <ErrorNotice error={error} />
        <button className="btn btn-primary btn-lg" type="submit" disabled={pending || !name.trim() || !agreed}>
          {pending ? 'Creating…' : page ? 'Continue to my page' : 'Create organisation'}
        </button>
        <div className="row-between small">
          {memberships.length ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreating(false)}>
              Back to my organisations
            </button>
          ) : (
            <span />
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </form>
    </AuthFrame>
  );
}
